import { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, TextField, Button, Typography, Box, Alert,
  Grid, CircularProgress, Link
} from '@mui/material';
import { useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';

export const PublicPage = () => {
  const { url } = useParams<{ url: string }>();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const createContact = useMutation(api.contacts.createPublicContact);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) {
      setError('Por favor, rellena los campos obligatorios');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('El formato del email no es válido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await createContact({ name, email, phone, url: url! });

      setSuccess(true);
      setTimeout(() => {
        window.location.href = result.immunocalUrl;
      }, 2000);
    } catch (err) {
      console.error('Error detallado:', err);
      setError(err instanceof Error ? err.message : 'Ha ocurrido un error inesperado. Por favor, contacta con soporte.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: '#f5f5f5' }}>
        <Grid container sx={{ minHeight: '100vh' }}>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Container maxWidth="md" sx={{ py: { xs: 4, md: 0 } }}>
              <Box sx={{ backgroundColor: 'white', borderRadius: 2, padding: { xs: 3, sm: 4 }, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxWidth: '600px', mx: 'auto' }}>
                <Alert severity="success" sx={{ borderRadius: 2, padding: 2, '& .MuiAlert-message': { fontSize: '1.1rem', textAlign: 'center', width: '100%' }, '& .MuiAlert-icon': { fontSize: '2rem' } }}>
                  ¡Gracias por tu interés! Estás siendo redirigido...
                </Alert>
              </Box>
            </Container>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' }, height: '100vh', position: 'relative' }}>
            <Box component="img" src="/img/fondofamilia.jpg" alt="Familia ImmunoWorld" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: '#f5f5f5' }}>
      <Grid container sx={{ minHeight: '100vh' }}>
        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', order: { xs: 2, md: 1 } }}>
          <Container maxWidth="md" sx={{ py: { xs: 4, md: 0 } }}>
            <Box sx={{ backgroundColor: 'white', borderRadius: 2, padding: { xs: 3, sm: 4 }, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxWidth: '600px', mx: 'auto' }}>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 3 }}>
                Espacio ImmunoWorld
              </Typography>
              <Typography variant="h6" sx={{ color: '#455a64', mb: 4, lineHeight: 1.5 }}>
                Accede a todos los detalles en segundos. Solo necesitamos tu nombre y correo
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', marginBottom: 3, borderRadius: 2 }}>{error}</Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField margin="normal" required fullWidth id="name" label="Nombre" name="name" autoFocus
                  value={name} onChange={(e) => setName(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }} />
                <TextField margin="normal" required fullWidth id="email" label="Email" name="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }} />
                <TextField margin="normal" fullWidth id="phone" label="Teléfono" name="phone"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }} />
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2">
                    Haz clic aquí para leer la <Link component={RouterLink} to="/aviso-legal" target="_blank">Política de Privacidad y el Aviso Legal</Link>
                  </Typography>
                </Box>
                <Button type="submit" fullWidth variant="contained" disabled={loading}
                  sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 2, backgroundColor: '#1a237e', fontSize: '1.1rem', textTransform: 'none', '&:hover': { backgroundColor: '#0d47a1' } }}>
                  {loading ? (
                    <><CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />Procesando...</>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.5 }}>He leido y acepto las condiciones</Typography>
                      <Typography>Acceder a los detalles</Typography>
                    </Box>
                  )}
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
        <Grid item xs={12} md={6} sx={{ height: { xs: '300px', md: '100vh' }, position: 'relative', order: { xs: 1, md: 2 } }}>
          <Box component="img" src="/img/fondofamilia.jpg" alt="Familia ImmunoWorld" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
        </Grid>
      </Grid>
    </Box>
  );
};
